import React, { useEffect, useRef, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import IconButton from '@material-ui/core/IconButton';
import PlayArrowIcon from '@material-ui/icons/PlayArrow';
import VolumeOffIcon from '@material-ui/icons/VolumeOff';
import VolumeUpIcon from '@material-ui/icons/VolumeUp';
import { useTranslation } from 'react-i18next';
import Spinner from 'components/Spinner/Spinner';
import { getThumbnailDownloadSignedUrl } from 'store/files/services/getThumbnailDownloadSignedUrl.service';
import { thumbnailUrlCache } from 'scenes/ProjectGallery/utils/thumbnailUrlCache';

// Reels-style video pane for the mobile pager. No native controls (the whole
// surface must stay swipeable): tap toggles play/pause (wired through
// videoApiRef by the gesture hook), a mute button, and a display-only
// progress line. Autoplays muted when promoted to center — the only
// autoplay mobile browsers reliably allow (same pattern as
// VideoPreviewComponent) — and remembers an unmute for the session.
//
// Two layers: the tiny looping .webm thumb (the gallery's video thumbnails
// are short webm clips, not stills) shows until the full video actually
// renders frames; the full video mounts with preload="auto" even as a side
// pane, so swiping to it starts near-instantly. Because panes are keyed by
// filePermission.id, this component instance survives promotion — the thumb
// keeps playing seamlessly while the full video spins up.

// Session-wide sound preference. Videos start muted; once the user unmutes,
// subsequent videos attempt unmuted playback (falling back to muted when the
// browser refuses — the preference itself is kept for the next video).
let preferredMuted = true;

const useStyles = makeStyles((theme) => ({
  root: {
    position: 'relative',
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundImage: `url(${theme.component.videoPlaceholder})`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'center',
  },
  thumbVideo: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    width: '100%',
    height: '100%',
    objectFit: 'contain',
  },
  // Same geometry as the image panes' media class — the video view must look
  // identical to the image view.
  fullVideo: {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
    userSelect: 'none',
    WebkitUserSelect: 'none',
    WebkitTouchCallout: 'none',
  },
  playGlyph: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 64,
    height: 64,
    borderRadius: '50%',
    background: 'rgba(0, 0, 0, 0.45)',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    pointerEvents: 'none',
    zIndex: 2,
  },
  progressTrack: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    height: 3,
    background: 'rgba(255, 255, 255, 0.25)',
    pointerEvents: 'none',
    zIndex: 2,
  },
  progressFill: {
    height: '100%',
    width: '0%',
    background: 'rgba(255, 255, 255, 0.85)',
    transition: 'width 250ms linear',
  },
  // Right edge below the next-chevron (which sits at 50%). The top
  // DialogActions bar is content-height (from md up its media-tag chips wrap
  // and grow it; below md, on a phone, it has none), so a fixed offset from
  // the top could end up underneath it; mid-height is clear of both the bar
  // and the comment overlay's usual reach.
  muteButton: {
    position: 'absolute',
    top: 'calc(50% + 48px)',
    right: 8,
    zIndex: 3,
    color: '#fff',
    background: 'rgba(0, 0, 0, 0.35)',
    '&:hover': {
      background: 'rgba(0, 0, 0, 0.5)',
    },
  },
}));

function MobileVideoPane({
  filePermission,
  isCenter,
  src,
  videoApiRef,
  onSourceError,
}) {
  const classes = useStyles();
  const { t } = useTranslation();

  const [muted, setMuted] = useState(preferredMuted);
  const [playing, setPlaying] = useState(false);
  const [fullReady, setFullReady] = useState(false);
  const [thumbSrc, setThumbSrc] = useState(
    () => thumbnailUrlCache.get(filePermission.id) || null
  );

  const videoRef = useRef(null);
  const progressRef = useRef(null);
  const cancelledRef = useRef(false);

  const attemptPlay = (v) => {
    const promise = v.play();
    if (!promise || !promise.catch) return;
    promise.catch((err) => {
      if (err && err.name === 'NotAllowedError' && !v.muted) {
        // Unmuted autoplay refused (iOS) — fall back to muted, but keep the
        // user's unmute preference for the next video.
        v.muted = true;
        if (!cancelledRef.current) setMuted(true);
        const retry = v.play();
        if (retry && retry.catch) retry.catch(() => {});
      }
      // Muted NotAllowedError = e.g. iOS low-power mode: stays paused, the
      // play glyph invites a tap. AbortError = pause/unmount raced play().
    });
  };

  // The pager can reach a video whose grid week never rendered — fetch the
  // thumb ourselves on a cache miss (same pattern as GalleryPictureItem).
  useEffect(() => {
    if (thumbSrc) return undefined;
    let cancelled = false;
    getThumbnailDownloadSignedUrl(filePermission.id)
      .then((url) => {
        if (cancelled || !url) return;
        thumbnailUrlCache.set(filePermission.id, url);
        setThumbSrc(url);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filePermission.id]);

  // Pause on teardown — dialog close (unmount) or a key={src} remount. A
  // playing media element removed from the document keeps playing its AUDIO
  // track (spec: it survives as "pending activity"), so an unmuted looping
  // video would sound on forever after the viewer closes.
  useEffect(() => {
    if (!src) return undefined;
    const v = videoRef.current;
    if (!v) return undefined;
    return () => {
      v.pause();
    };
  }, [src]);

  // Reset media state whenever the full element is replaced or removed
  // (TTL remount, error-retry clearing src) so the thumb layer covers the
  // gap and the fresh element stays hidden until its own onPlaying.
  useEffect(() => {
    setFullReady(false);
    setPlaying(false);
  }, [src]);

  // Demotion: pause and rewind (TikTok restarts a revisited video) but keep
  // the element mounted — its buffer makes the next swipe-in instant.
  useEffect(() => {
    if (isCenter) return;
    const v = videoRef.current;
    if (v) {
      v.pause();
      try {
        v.currentTime = 0;
      } catch (err) {
        // no metadata yet — nothing to rewind
      }
    }
    setFullReady(false);
    setPlaying(false);
  }, [isCenter]);

  // Promotion boot: apply the session sound preference and autoplay.
  useEffect(() => {
    if (!isCenter || !src) return;
    const v = videoRef.current;
    if (!v) return;
    // React 16 doesn't propagate `muted` prop updates to the DOM
    // (facebook/react#10389) — manage it imperatively.
    v.muted = preferredMuted;
    setMuted(preferredMuted);
    attemptPlay(v);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCenter, src]);

  // Expose play/pause to the gesture hook's tap handling.
  useEffect(() => {
    if (!isCenter || !videoApiRef) return undefined;
    const api = {
      togglePlay: () => {
        const v = videoRef.current;
        if (!v) return;
        if (v.paused) attemptPlay(v);
        else v.pause();
      },
    };
    videoApiRef.current = api;
    return () => {
      if (videoApiRef.current === api) videoApiRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCenter, videoApiRef]);

  useEffect(
    () => () => {
      cancelledRef.current = true;
    },
    []
  );

  const toggleMute = () => {
    const v = videoRef.current;
    const next = !(v ? v.muted : muted);
    if (v) v.muted = next;
    preferredMuted = next;
    setMuted(next);
  };

  const handleTimeUpdate = (e) => {
    const v = e.target;
    if (progressRef.current && v.duration) {
      progressRef.current.style.width = `${
        (v.currentTime / v.duration) * 100
      }%`;
    }
  };

  const showThumb = !(isCenter && fullReady);

  return (
    <div className={classes.root}>
      {showThumb && thumbSrc ? (
        <video
          key="thumb"
          className={classes.thumbVideo}
          muted
          loop
          playsInline
          autoPlay
        >
          <source src={thumbSrc} type="video/webm" />
        </video>
      ) : null}
      {src ? (
        <video
          key={src}
          ref={videoRef}
          className={classes.fullVideo}
          style={{
            visibility: isCenter && fullReady ? 'visible' : 'hidden',
          }}
          preload="auto"
          loop
          playsInline
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
          onPlaying={() => setFullReady(true)}
          onTimeUpdate={handleTimeUpdate}
          onError={onSourceError}
        >
          <source
            src={src}
            type={filePermission.file.mimeType}
            onError={onSourceError}
          />
        </video>
      ) : null}
      {isCenter && !src ? <Spinner /> : null}
      {isCenter && src && !playing ? (
        <div className={classes.playGlyph} aria-hidden>
          <PlayArrowIcon fontSize="large" />
        </div>
      ) : null}
      {isCenter && src ? (
        <div className={classes.progressTrack}>
          <div ref={progressRef} className={classes.progressFill} />
        </div>
      ) : null}
      {isCenter && src ? (
        <IconButton
          className={classes.muteButton}
          size="small"
          aria-label={
            muted ? t('gallery.unmute', 'Unmute') : t('gallery.mute', 'Mute')
          }
          onClick={toggleMute}
        >
          {muted ? <VolumeOffIcon /> : <VolumeUpIcon />}
        </IconButton>
      ) : null}
    </div>
  );
}

export default MobileVideoPane;
